import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { QuoteItemDto } from '../../generated';
import { API_BASE_URL } from '../../config/api';

export type QuoteItem = QuoteItemDto;

export interface QuoteItemQueryParams {
  quoteId?: number;
}

interface QuoteItemState {
  items: QuoteItem[];
  selectedItem: QuoteItem | null;
  loading: boolean;
  operationLoading: {
    list: boolean;
    detail: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;
  errors: {
    list: string | null;
    detail: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
  };
}

const getAuthHeaders = (): HeadersInit => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0',
});

const initialState: QuoteItemState = {
  items: [],
  selectedItem: null,
  loading: false,
  operationLoading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  errors: {
    list: null,
    detail: null,
    create: null,
    update: null,
    delete: null,
  },
};

/** GET /api/QuoteItem?quoteId= */
export const getQuoteItems = createAsyncThunk(
  'quoteItem/getQuoteItems',
  async (params: QuoteItemQueryParams = {}, thunkAPI) => {
    try {
      const query = new URLSearchParams();
      if (params.quoteId != null) {
        query.append('quoteId', String(params.quoteId));
      }
      const url = `${API_BASE_URL}/api/QuoteItem${query.toString() ? `?${query.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return { data, params };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Teklif kalemleri yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

/** GET /api/QuoteItem/{id} */
export const getQuoteItemById = createAsyncThunk(
  'quoteItem/getQuoteItemById',
  async (id: number, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/QuoteItem/${id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Teklif kalemi yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

/** POST /api/QuoteItem */
export const createQuoteItem = createAsyncThunk(
  'quoteItem/createQuoteItem',
  async (dto: QuoteItemDto, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/QuoteItem`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dto),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Teklif kalemi oluşturulurken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

/** PUT /api/QuoteItem/{id} */
export const updateQuoteItem = createAsyncThunk(
  'quoteItem/updateQuoteItem',
  async ({ id, data }: { id: number; data: QuoteItemDto }, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/QuoteItem/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const result = await response.json();
      return { id, data: result };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Teklif kalemi güncellenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

/** DELETE /api/QuoteItem/{id} */
export const deleteQuoteItem = createAsyncThunk(
  'quoteItem/deleteQuoteItem',
  async (id: number, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/QuoteItem/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      // Backend, silme sonrası checkout'un tetiklenip tetiklenmediğini mesajda bildiriyor
      let message = '';
      try {
        const body = await response.json();
        message = typeof body?.message === 'string' ? body.message : '';
      } catch {
        // Gövde okunamazsa çağıran taraf kendi varsayılan mesajını gösterir
      }
      return { id, message };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Teklif kalemi silinirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

function normalizeListPayload(payload: unknown): QuoteItem[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data?: unknown }).data;
    if (Array.isArray(data)) {
      return data;
    }
  }
  return [];
}

function normalizeItemPayload(payload: unknown): QuoteItem | null {
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return payload as QuoteItem;
  }
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data?: unknown }).data;
    if (data && typeof data === 'object' && data !== null && 'id' in data) {
      return data as QuoteItem;
    }
  }
  return null;
}

const quoteItemSlice = createSlice({
  name: 'quoteItem',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.errors = {
        list: null,
        detail: null,
        create: null,
        update: null,
        delete: null,
      };
    },
    clearSpecificError: (state, action: PayloadAction<keyof QuoteItemState['errors']>) => {
      state.errors[action.payload] = null;
    },
    clearQuoteItems: (state) => {
      state.items = [];
      state.error = null;
      state.errors.list = null;
    },
    clearSelectedQuoteItem: (state) => {
      state.selectedItem = null;
      state.errors.detail = null;
    },
  },
  extraReducers: (builder) => {
    // getQuoteItems
    builder.addCase(getQuoteItems.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getQuoteItems.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = normalizeListPayload(action.payload.data);
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getQuoteItems.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Teklif kalemleri yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    // getQuoteItemById
    builder.addCase(getQuoteItemById.pending, (state) => {
      state.operationLoading.detail = true;
      state.error = null;
      state.errors.detail = null;
    });
    builder.addCase(getQuoteItemById.fulfilled, (state, action) => {
      state.operationLoading.detail = false;
      state.selectedItem = normalizeItemPayload(action.payload) ?? null;
      state.error = null;
      state.errors.detail = null;
    });
    builder.addCase(getQuoteItemById.rejected, (state, action) => {
      state.operationLoading.detail = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Teklif kalemi yüklenirken hata oluştu';
      state.error = msg;
      state.errors.detail = msg;
    });

    // createQuoteItem
    builder.addCase(createQuoteItem.pending, (state) => {
      state.operationLoading.create = true;
      state.error = null;
      state.errors.create = null;
    });
    builder.addCase(createQuoteItem.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      const item = normalizeItemPayload(action.payload);
      if (item?.id != null) {
        const idx = state.items.findIndex((i) => i.id === item.id);
        if (idx === -1) {
          state.items.unshift(item);
        } else {
          state.items[idx] = item;
        }
      }
      state.error = null;
      state.errors.create = null;
    });
    builder.addCase(createQuoteItem.rejected, (state, action) => {
      state.operationLoading.create = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Teklif kalemi oluşturulurken hata oluştu';
      state.error = msg;
      state.errors.create = msg;
    });

    // updateQuoteItem
    builder.addCase(updateQuoteItem.pending, (state) => {
      state.operationLoading.update = true;
      state.error = null;
      state.errors.update = null;
    });
    builder.addCase(updateQuoteItem.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      const { id, data } = action.payload;
      const item = normalizeItemPayload(data);
      if (item?.id != null) {
        const idx = state.items.findIndex((i) => i.id === id);
        if (idx !== -1) {
          state.items[idx] = item;
        }
        if (state.selectedItem?.id === id) {
          state.selectedItem = item;
        }
      }
      state.error = null;
      state.errors.update = null;
    });
    builder.addCase(updateQuoteItem.rejected, (state, action) => {
      state.operationLoading.update = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Teklif kalemi güncellenirken hata oluştu';
      state.error = msg;
      state.errors.update = msg;
    });

    // deleteQuoteItem
    builder.addCase(deleteQuoteItem.pending, (state) => {
      state.operationLoading.delete = true;
      state.error = null;
      state.errors.delete = null;
    });
    builder.addCase(deleteQuoteItem.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      state.items = state.items.filter((i) => i.id !== action.payload.id);
      if (state.selectedItem?.id === action.payload.id) {
        state.selectedItem = null;
      }
      state.error = null;
      state.errors.delete = null;
    });
    builder.addCase(deleteQuoteItem.rejected, (state, action) => {
      state.operationLoading.delete = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Teklif kalemi silinirken hata oluştu';
      state.error = msg;
      state.errors.delete = msg;
    });
  },
});

export const {
  clearError,
  clearSpecificError,
  clearQuoteItems,
  clearSelectedQuoteItem,
} = quoteItemSlice.actions;

export default quoteItemSlice.reducer;
